<template>
  <div class="availability-info-wrapper">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="availability-info-wrapper"
        :show-close-button="false"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>{{ $t("availability-info.title") }}</SDialogTitle>
        </SDialogHeader>
        <p>{{ $t("availability-info.description") }}</p>
        <div v-if="noAvailabilityInfo && noAvailabilityInfo.length">
          <div
            v-for="info in noAvailabilityInfo"
            :key="String(info.categoryId) + String(info.priceId)"
            class="info-row"
          >
            <p>
              <strong>{{ info.categoryName }} - {{ info.priceName }}</strong>
            </p>
            <p>
              {{ $t("availability-info.available") }}
              {{ info.dbAvailability }}
            </p>
            <p>{{ $t("availability-info.booked") }} {{ info.bookedQty }}</p>
          </div>
        </div>
        <SDialogFooter class="sm:justify-center">
          <SButton type="button" size="lg" @click="pssRemovalConfirmation()">
            {{ $t("availability-info.ok-button") }}
          </SButton>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { ModalStatus } from "@/constants/modal";
import BookingModule from "@/store/modules/booking";
import { IAvailabilityInfo } from "@/types/db/product-slot-state";
import { deleteUnconfirmedTickets } from "@/utils/vue-helpers/booking";
import { IBookingsSteps } from "@/interfaces/store/IBooking";
import { BookingStep } from "@/constants/booking";
import { useI18n } from "vue-i18n";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "AvailabilityInfo",
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    const { t } = useI18n({});
    const dialogVisible = dialogVisibleForStatus(ModalStatus.AvailabilityInfo);
    const noAvailabilityInfo = computed(() => {
      const bookingAvailabilityInfo: IAvailabilityInfo[] =
        BookingModule.bookingAvailabilityInfo;
      const noAv: IAvailabilityInfo[] = bookingAvailabilityInfo?.filter(
        (info) => info.bookedQty > info.dbAvailability && !info.sellOnSoldOut
      );
      if (!noAv || !noAv.length) {
        return [];
      }

      return noAv;
    });

    return { dialogVisible, noAvailabilityInfo };
  },
  mounted() {
    if (!this.noAvailabilityInfo || !this.noAvailabilityInfo.length)
      this.closeModal();
  },
  methods: {
    closeModal() {
      this.dialogVisible = false;
    },
    async removeUnavailablePSS() {
      // for each price that is not available remove its pss from the current booking
      for (const info of this.noAvailabilityInfo) {
        const pssListToDelete: string[] = info.pssIds;
        // lets also delete all the related add-ons
        const pssList = BookingModule.selectedBooking?.pssList || [];
        const addonsPSSIds: string[] = pssList
          .filter(
            (pss) =>
              pss.mainTicketUUID && info.pssIds.includes(pss.mainTicketUUID)
          )
          .map((pss) => String(pss.uuid));
        pssListToDelete.push(...addonsPSSIds);
        const releaseSeats = true; // We need to release any seats related to their pss
        await deleteUnconfirmedTickets(pssListToDelete, releaseSeats);
      }
    },
    async pssRemovalConfirmation() {
      await this.removeUnavailablePSS();
      // Go back to order step if there is no poss remaining for the booking
      if (
        !BookingModule.selectedBooking?.pssList ||
        !BookingModule.selectedBooking?.pssList.length
      ) {
        // Clear bookingAvailabilityInfo object from the state
        BookingModule.updateAvailabilityInfo([]);
        // Move back to Order step
        BookingModule.setBookingStep({
          bookingUUID: BookingModule.selectedBookingUUID,
          step: BookingStep.Order,
        });
      }
      this.closeModal();
    },
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;
.availability-info-wrapper {
  p {
    font-size: 15px;
    word-break: normal;
  }
  .info-row {
    p {
      font-size: 13px;
      letter-spacing: 1px;
    }
  }
}
</style>
